import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { HoistedSkillInfo } from '../agent'
import { agentSkillsApi, type SkillBundle } from '../services/agentSkillsApi'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import type { SkillDraft, SkillDuplicateLevel, SkillInfo, SkillLibraryResponse } from '../types/agent'

/** How long the Skills tab waits for a distilled draft to reach the catalog. */
export const HOIST_WAIT_MS = 15_000
export const HOIST_POLL_MS = 2_000

/**
 * Which draft a hoist produced, once the catalog has it. The runner catalogs
 * the draft after the browser has seen skill_hoisted, so the draft is "the
 * one named like the hoist whose content changed since the hoist was seen"
 * (a re-hoist overwrites an existing draft of that name). If the content
 * never changes (the agent wrote the same thing again) the existing draft
 * counts once the wait is nearly over.
 */
export function findHoistedDraft(
  drafts: SkillInfo[],
  hoisted: HoistedSkillInfo | null,
  seen: { hash: string | undefined; at: number } | null,
  now: number,
): SkillInfo | undefined {
  if (!hoisted || !seen) return undefined
  const draft = drafts.find((d) => d.name === hoisted.name)
  if (!draft) return undefined
  if (draft.content_hash !== seen.hash) return draft
  return now - seen.at >= HOIST_WAIT_MS - HOIST_POLL_MS ? draft : undefined
}

const EMPTY: SkillLibraryResponse = { company: [], bayesprice: [], mine: [], drafts: [], colleagues: [], can_promote: false, can_manage_bayesprice: false }

/** Every tier an array, whatever answered (a missing or odd body reads as an empty library). */
function normalizeLibrary(raw: Partial<SkillLibraryResponse> | undefined): SkillLibraryResponse {
  const list = (v: unknown) => (Array.isArray(v) ? (v as SkillInfo[]) : [])
  if (!raw || typeof raw !== 'object') return EMPTY
  return {
    company: list(raw.company), bayesprice: list(raw.bayesprice), mine: list(raw.mine), drafts: list(raw.drafts), colleagues: list(raw.colleagues),
    can_promote: raw.can_promote === true,
    can_manage_bayesprice: raw.can_manage_bayesprice === true,
  }
}

/**
 * A customer's skills library (B2): the Skills tab on every agent page and
 * Admin → Skills read it, and write through it. All share one query per
 * customer, so a save in one place reaches the others.
 *
 * With `hoisted` (the session's last skill_hoisted), the library is polled
 * until the draft that hoist produced is in the catalog, for at most
 * HOIST_WAIT_MS; `hoistedDraft` is that draft.
 */
export const useAgentSkills = ({ customer, sessionId, job, hoisted = null, enabled = true }: {
  customer: string
  sessionId?: string
  job?: string
  hoisted?: HoistedSkillInfo | null
  enabled?: boolean
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const baseKey = ['agent-skills', customer]

  // When a new hoist is first seen: the draft of that name as it was then.
  const seenRef = useRef<{ key: string; hash: string | undefined; at: number } | null>(null)

  const libraryQuery = useQuery({
    queryKey: [...baseKey, sessionId ?? '', job ?? ''],
    enabled: Boolean(enabled && customer && account.user),
    queryFn: async () => {
      try {
        return await agentSkillsApi.library(customer, { sessionId, job })
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    refetchInterval: (query) => {
      const seen = seenRef.current
      if (!hoisted || !seen) return false
      const drafts = normalizeLibrary(query.state.data).drafts
      if (findHoistedDraft(drafts, hoisted, seen, Date.now())) return false
      return Date.now() - seen.at < HOIST_WAIT_MS ? HOIST_POLL_MS : false
    },
  })
  const data = normalizeLibrary(libraryQuery.data)

  // Render-phase guard: snapshot the draft once per hoist.
  const hoistKey = hoisted ? `${hoisted.name}@${hoisted.at}` : ''
  if (hoisted && seenRef.current?.key !== hoistKey) {
    seenRef.current = { key: hoistKey, hash: data.drafts.find((d) => d.name === hoisted.name)?.content_hash, at: Date.now() }
  }
  const hoistedDraft = findHoistedDraft(data.drafts, hoisted, seenRef.current, Date.now())

  const invalidate = () => queryClient.invalidateQueries({ queryKey: baseKey })
  const onError = (error: Error) => snackbar.error(extractErrorMessage(error))
  const succeeded = (message?: string) => () => {
    invalidate()
    if (message) snackbar.success(message)
  }

  const createMutation = useMutation({
    mutationFn: (draft: SkillDraft) => agentSkillsApi.create(customer, draft),
    onSuccess: succeeded(),
    onError,
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: SkillDraft }) => agentSkillsApi.update(id, customer, draft),
    onSuccess: succeeded(),
    onError,
  })
  const publishMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.publish(id, customer),
    onSuccess: succeeded('Saved to My skills'),
    onError,
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.remove(id, customer),
    onSuccess: succeeded('Skill deleted'),
    onError,
  })
  const promoteMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.promote(id, customer),
    onSuccess: succeeded('Skill is now Company-level: the agent uses it for everyone'),
    onError,
  })
  const demoteMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.demote(id, customer),
    onSuccess: succeeded('Skill is no longer Company-level'),
    onError,
  })
  const copyMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.copy(id, customer),
    onSuccess: succeeded('Copy saved to My skills'),
    onError,
  })
  const moveToBayesPriceMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.promotePublic(id, customer),
    onSuccess: succeeded('Moved to Bayes Price skills: every customer can use it'),
    onError,
  })
  const duplicateMutation = useMutation({
    mutationFn: ({ id, level }: { id: string; level: SkillDuplicateLevel }) => agentSkillsApi.duplicate(id, customer, level),
    onSuccess: (_copy, { level }) => succeeded(level === 'organisation' ? 'Duplicate saved to Company-level' : 'Duplicate saved to My skills')(),
    onError,
  })
  const setOffMutation = useMutation({
    mutationFn: ({ id, off }: { id: string; off: boolean }) => agentSkillsApi.setOffForCustomer(id, customer, off),
    onSuccess: (_data, { off }) => succeeded(off ? `Skill turned off for ${customer}` : `Skill turned on for ${customer}`)(),
    onError,
  })
  const publishToBayesPriceMutation = useMutation({
    mutationFn: (id: string) => agentSkillsApi.publishToBayesPrice(id, customer),
    onSuccess: succeeded('Saved to Bayes Price skills'),
    onError,
  })

  return {
    company: data.company,
    bayesprice: data.bayesprice,
    mine: data.mine,
    drafts: data.drafts,
    colleagues: data.colleagues,
    canPromote: data.can_promote,
    canManageBayesPrice: data.can_manage_bayesprice,
    isLoading: libraryQuery.isLoading,
    error: libraryQuery.error ? libraryQuery.error.message : null,
    /** The draft the session's last hoist produced, once it is in the catalog. */
    hoistedDraft,
    handleCreate: createMutation.mutateAsync,
    handleUpdate: (id: string, draft: SkillDraft) => updateMutation.mutateAsync({ id, draft }),
    handlePublish: publishMutation.mutateAsync,
    handleDelete: deleteMutation.mutateAsync,
    handlePromote: promoteMutation.mutateAsync,
    handleDemote: demoteMutation.mutateAsync,
    handleCopy: copyMutation.mutateAsync,
    handleMoveToBayesPrice: moveToBayesPriceMutation.mutateAsync,
    handleDuplicate: (id: string, level: SkillDuplicateLevel) => duplicateMutation.mutateAsync({ id, level }),
    handleSetOff: (id: string, off: boolean) => setOffMutation.mutateAsync({ id, off }),
    handlePublishToBayesPrice: publishToBayesPriceMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending || publishMutation.isPending,
  }
}

/**
 * One skill's stored files (SKILL.md, manifest, files/…), for the
 * viewer and the editor. Keyed by content hash, so an edit or a re-hoist
 * reads the new bundle.
 */
export const useSkillBundle = (skill: SkillInfo | null, customer: string) =>
  useQuery<SkillBundle>({
    queryKey: ['agent-skill-bundle', customer, skill?.id ?? '', skill?.content_hash ?? ''],
    enabled: Boolean(skill && customer),
    queryFn: async () => {
      try {
        return await agentSkillsApi.bundle(encodeURIComponent(skill!.id), customer)
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

export default useAgentSkills
